/**
 * Two-axis variant selector (Color swatches + Size buttons) shared by the
 * product card and the product detail page.
 *
 * Printful-style variant titles look like "Product Name / Color / Size":
 * with 3+ segments the last two are [color, size]; with 2 segments the last
 * one is a single option axis (relabelled "Option" when the values don't
 * look like garment sizes).
 *
 * License: AGPL-3.0-or-later (see repository LICENSE)
 */

import { Button } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { StoreVariant } from "@/lib/store-api";

export function parseVariantOptions(title: string): { color?: string; size?: string } {
  const segs = title.split(" / ").map((s) => s.trim()).filter(Boolean);
  if (segs.length >= 3) return { color: segs[segs.length - 2], size: segs[segs.length - 1] };
  if (segs.length === 2) return { size: segs[1] };
  return {};
}

const KNOWN_SIZES = new Set([
  "XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL", "4XL", "5XL",
  "ONE SIZE", "OS",
]);

/** Common Printful garment colors → hex for swatches. */
const COLOR_HEX: Record<string, string> = {
  "black": "#111111",
  "white": "#f5f5f5",
  "navy": "#212a41",
  "charcoal": "#3b4045",
  "forest green": "#1f4d36",
  "forest": "#1f4d36",
  "red": "#b22222",
  "dark grey heather": "#4a4a4a",
  "dark heather": "#4a4a4a",
  "athletic heather": "#9aa0a6",
  "heather grey": "#b3b6ba",
  "sport grey": "#9aa0a6",
  "maroon": "#5c2233",
  "brown": "#5c4033",
  "sand": "#d8c6a5",
  "natural": "#e8e0d0",
  "olive": "#556b2f",
  "military green": "#4b5320",
  "army": "#4b5320",
  "asphalt": "#43464b",
  "gold": "#b8860b",
  "orange": "#cc5500",
  "purple": "#4b2e6f",
  "burgundy": "#6d1a36",
  "royal": "#1d4ed8",
  "royal blue": "#1d4ed8",
  "light blue": "#a9c6e8",
  "pink": "#d8a0b0",
  "yellow": "#d9b611",
  "steel blue": "#4f6d8f",
  "dark chocolate": "#3b2a20",
  "true royal": "#1d4ed8",
  "kelly": "#2e8b57",
  "silver": "#c0c0c0",
  "ash": "#d5d8d2",
};

function colorToHex(name: string): string | null {
  return COLOR_HEX[name.toLowerCase()] ?? null;
}

interface VariantSelectorProps {
  variants: StoreVariant[];
  selectedSku: string;
  onSelect: (sku: string) => void;
  /** "sm" for cards, "md" for the product detail page */
  size?: "sm" | "md";
}

export function VariantSelector({
  variants,
  selectedSku,
  onSelect,
  size = "sm",
}: VariantSelectorProps) {
  const { t } = useTranslation();

  if (variants.length <= 1) return null;

  const parsed = variants.map((v) => ({ v, ...parseVariantOptions(v.title) }));
  const colors = [...new Set(parsed.map((p) => p.color).filter(Boolean))] as string[];
  const sizes = [...new Set(parsed.map((p) => p.size).filter(Boolean))] as string[];
  const current =
    parsed.find((p) => p.v.sku === selectedSku) ?? parsed[0];

  const singleAxis = colors.length === 0;
  const singleAxisIsSize =
    singleAxis && sizes.every((s) => KNOWN_SIZES.has(s.toUpperCase()));

  const pick = (color?: string, sizeOpt?: string) => {
    const match =
      parsed.find((p) => p.color === color && p.size === sizeOpt) ||
      parsed.find((p) => p.color === color) ||
      parsed.find((p) => p.size === sizeOpt);
    if (match) onSelect(match.v.sku);
  };

  const md = size === "md";
  const swatch = md ? "w-7 h-7" : "w-5 h-5";
  const btn = md ? "text-sm h-8 px-2.5 min-w-9" : "text-xs h-6 px-1.5 min-w-7";
  const label = md
    ? "text-sm font-medium text-default-700 w-14 shrink-0"
    : "text-xs text-default-500 w-10 shrink-0";

  return (
    <div className={md ? "space-y-3" : "space-y-2 mt-1"}>
      {colors.length > 0 && (
        <div className="flex items-center flex-wrap gap-1.5">
          <span className={label}>{t("product-color", "Color")}</span>
          {colors.map((c) => {
            const hex = colorToHex(c);
            const isSelected = current?.color === c;
            return hex ? (
              <button
                key={c}
                type="button"
                title={c}
                aria-label={c}
                onClick={() => pick(c, current?.size)}
                className={`${swatch} rounded-full border transition-shadow ${
                  isSelected
                    ? "border-primary ring-2 ring-primary/60"
                    : "border-separator hover:ring-1 hover:ring-default-400"
                }`}
                style={{ backgroundColor: hex }}
              />
            ) : (
              <Button
                key={c}
                size="sm"
                variant={isSelected ? "primary" : "outline"}
                onPress={() => pick(c, current?.size)}
                className={`${btn}`}
              >
                {c}
              </Button>
            );
          })}
          {md && current?.color && (
            <span className="text-sm text-default-500 ml-1">{current.color}</span>
          )}
        </div>
      )}
      {sizes.length > 0 && (
        <div className="flex items-center flex-wrap gap-1">
          <span className={label}>
            {singleAxis && !singleAxisIsSize
              ? t("product-option", "Option")
              : t("product-size", "Size")}
          </span>
          {sizes.map((s) => {
            const isSelected = current?.size === s;
            const available =
              singleAxis ||
              parsed.some((p) => p.color === current?.color && p.size === s);
            return (
              <Button
                key={s}
                size="sm"
                isDisabled={!available}
                variant={isSelected ? "primary" : "outline"}
                onPress={() => pick(current?.color, s)}
                className={btn}
              >
                {s}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}
